import { Component, Host, h, State, Prop } from "@stencil/core";
import { getStore, ILiftStatusStore } from "../../store";
import { sensorInfo } from "../../utils/sensorInfo";
import {
  ILiftStatusSensorDataResponse,
  ILiftStatusValues,
} from "../../utils/types";
import { isNumber } from "lodash-es";
import { watchStoreLocale } from "../../utils/locale-state";

@Component({
  tag: "ls-indicator",
  styleUrl: "ls-indicator.css",
  shadow: true,
})
export class LsIndicator {
  /**
   * The ID of the sensor that is to be displayed.
   */
  @Prop() readonly sensorId: number;

  @State() sensorData: ILiftStatusValues;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  private store: ILiftStatusStore;
  private storeUnsubscribe: (() => void) | null = null;

  private get dataLabel(): string {
    return sensorInfo.getShortLabel(this.sensorId, this.store?.state.locale);
  }

  private processSensorUpdate = (sensorData: ILiftStatusSensorDataResponse) => {
    const sensorValue = sensorData.values[this.sensorId];

    // The sensor values of 501 and 514 and 515 are zero based, but we need to show them one based
    this.sensorData = isNumber(sensorValue)
      ? sensorValue + 1
      : sensorValue === "¿"
      ? "?" // Our font doesn't support the upside down question mark
      : sensorValue;
  };

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store);

    this.processSensorUpdate(this.store.state.currentSensorData);

    this.storeUnsubscribe = this.store.onChange("currentSensorData", (sensorData) => {
      this.processSensorUpdate(sensorData);
    });
  }

  disconnectedCallback() {
    if (this.storeUnsubscribe !== null) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }
  }

  render() {
    if (this.sensorId === null) {
      console.group(
        "%cError%c [LsIndicator] No sensor ID supplied",
        "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
        ""
      );
      console.log(
        `Add the ID to the LsIndicator component like so: %c<ls-indicator sensor-id="yourId"></ls-indicator>%c`,
        "color:#27272A; background-color: #E4E4E7; display: inline-block; padding: 0 4px; border-radius: 2px;",
        ""
      );
      console.groupEnd();
    }

    return (
      <Host>
        <div class="label">
          {this.sensorId === null
            ? this.store.t("ui:indicator.no_id")
            : this.dataLabel}
        </div>
        <div class="backdrop">00</div>
        <div class="indicator">
          {!this.store.state.error.hasError && (this.sensorData || "")}
        </div>
      </Host>
    );
  }

}
